import { useState, useRef, useCallback, useEffect } from 'react';

export const useMicrophoneTest = (audioDeviceId?: string | null) => {
  const [isTesting, setIsTesting] = useState(false);
  const [volume, setVolume] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const analyzeVolume = useCallback(() => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);

    // Вычисляем среднюю громкость
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    const average = sum / dataArray.length;
    
    // Нормализуем в диапазон 0-100
    const normalizedVolume = Math.min(100, (average / 255) * 200);
    setVolume(normalizedVolume);

    // Продолжаем анализ
    animationFrameRef.current = requestAnimationFrame(analyzeVolume);
  }, []);

  const startTest = useCallback(async () => {
    if (isTesting) {
      // Останавливаем тест
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (audioContextRef.current) {
        await audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      setVolume(0);
      setIsTesting(false);
      return;
    }

    try {
      // Получаем доступ к микрофону
      const constraints: MediaStreamConstraints = {
        audio: audioDeviceId 
          ? { deviceId: { exact: audioDeviceId } }
          : true,
        video: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Создаем AudioContext и Analyser
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;

      source.connect(analyser);
      analyserRef.current = analyser;

      setIsTesting(true);
      
      // Начинаем анализ
      analyzeVolume();
    } catch (error) {
      console.error('Failed to start microphone test:', error);
      setIsTesting(false);
    }
  }, [isTesting, audioDeviceId, analyzeVolume]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return { isTesting, volume, startTest };
};
